import {
  memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import Box from '@mui/joy/Box';
import { Button, Typography } from '@mui/joy';

import { IPageGift as IPageGiftData } from 'app/store/nodes/card/interfaces';
import { useDispatcher, useSelector } from 'app/store/utils/redux/hooks';
import { useMediaResource } from 'lib/MediaHooks';
import { PhysicalGiftType } from 'lib/provider/services/data-adapter/types';
import giftBoxImage from 'assets/image/giftbox.png';

type PagePhysicalGiftProps = {
  data?: IPageGiftData<PhysicalGiftType>,
};

const PagePhysicalGift = (props: PagePhysicalGiftProps) => {
  const {
    data,
  } = props;
  const dispatcher = useDispatcher();
  const gift = data?.items?.gift;
  const isLoading = useSelector((state) => state?.gift?.isLoading);
  const [isClosed, setIsClosed] = useState<boolean>(gift?.status === 'closed' || false);

  const { containerRef: containerImageRef } = useMediaResource(gift?.image);

  useEffect(() => {
    if (gift?.status === 'closed') {
      setIsClosed(true);
      return undefined;
    }
    let timeout: any = null;
    // applying with delay
    if (gift?.status === 'complete') {
      timeout = setTimeout(() => {
        setIsClosed(false);
      }, 6000);
      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }
    return undefined;
  }, [gift?.status]);

  const handleViewGiftStatusClick = useCallback(() => {
    dispatcher.gift.useGift();
  }, []);

  const imageProps = useMemo(() => ({
    maxWidth: '18em',
    alignSelf: 'center',
    mt: '3em',
    borderRadius: 2,
    sx: {
      '& img': {
        width: 'auto!important',
        maxHeight: '12em!important',
        boxShadow: (theme: any) => `${theme.shadow.xs}, ${theme.shadow.sm}`,
        borderRadius: (theme: any) => theme.radius.sm,
      },
      '& .gift-state-closed': {
        display: !isClosed ? 'none' : undefined,
      },
      '& [data-type="media-image"]': {
        display: isClosed ? 'none' : undefined,
      },
    },
  }), [isClosed]);

  if (!gift) {
    return (
      <Box bgcolor="#ffffff" flex={1} />
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      bgcolor="#ffffff"
      flex={1}
      alignItems="space-between"
      sx={{
        '&': {
          pointerEvents: 'all',
          fontSize: '1.6em',
          '--Button-paddingBlock': '0.625em',
          '--Button-radius': '1.375em',
          '--joy-fontSize-sm': '1em',
        },
        '& .MuiButton-root': {
          paddingInline: '2em',
        },
      }}
    >
      <Box ref={containerImageRef} {...imageProps} height="12em">
        <img className="gift-state-closed" src={giftBoxImage} alt={gift.name} style={{ boxShadow: 'none' }} />
      </Box>
      <Box display="flex" flexDirection="column" flex={1} alignItems="center">
        {isClosed && (
          <Typography fontWeight={500} fontSize="1.125em" mx="2em" textAlign="center" sx={{ textWrap: 'balance' }} mt="1em">{gift.message}</Typography>
        )}
        {!isClosed && (
          <Typography fontWeight={500} fontSize="1.125em" mx="2em" textAlign="center" sx={{ textWrap: 'balance' }} mt="1em">{gift.name}</Typography>
        )}
      </Box>
      <Button
        sx={{ alignSelf: 'center', mb: '4em', boxShadow: (theme) => theme.shadow.sm }}
        onClick={handleViewGiftStatusClick}
        loading={isLoading}
      >
        {isClosed && 'Open Gift'}
        {!isClosed && 'View Gift Status'}
      </Button>
    </Box>
  );
};

export default memo(PagePhysicalGift);
